import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useProperty } from "@/context/PropertyContext";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { eur, fechaCorta, MESES_NOMBRE } from "@/lib/format";
import { toast } from "sonner";

const DAY_MS = 24 * 60 * 60 * 1000;
const GRACE_DAYS = 5;

// Un pago se considera "de alquiler" (y por tanto encaja en la matriz mensual
// por inquilino) si su concepto lo indica o si viene de un contrato; los
// relacionados con fianza se tratan aparte porque no son recurrentes.
function isRentPayment(p) {
  const c = String(p.concepto || "").toLowerCase();
  if (c.includes("fianza")) return false;
  if (c.includes("alquiler") || c.includes("mensual")) return true;
  return !!p.contract_id;
}

const FIANZA_CONCEPTO = "Fianza";
const FIANZA_DEVUELTA_CONCEPTO = "Fianza devuelta";
function isFianzaRetenidaConcepto(c) {
  return String(c || "").toLowerCase().startsWith("fianza retenida");
}

const ESTADO_FIANZA_META = {
  pendiente: { label: "En curso", badge: "bg-primary/15 text-primary" },
  retenida: { label: "Retenida", badge: "bg-warning/15 text-warning" },
  devuelta: { label: "Devuelta", badge: "bg-success/15 text-success" },
  probable_devuelta: { label: "Devuelta (estimado)", badge: "bg-success/10 text-success/80" },
  sin_resolver: { label: "Sin resolver", badge: "bg-destructive/15 text-destructive" },
};

// Umbral del fallback: si nadie marcó devolución ni retención pero el
// inquilino estuvo al menos un año en el piso (sumando todos sus
// contratos, no solo el último), lo normal es que la fianza se devolviera
// sin más y solo falte anotarlo — a diferencia de una estancia corta sin
// marcar, que sí es sospechosa de un olvido real.
const ESTANCIA_MINIMA_DEVOLUCION_DIAS = 365;

function diasEntre(inicioIso, finIso) {
  const a = new Date(inicioIso);
  const b = new Date(finIso);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  return Math.max(0, (b.getTime() - a.getTime()) / DAY_MS);
}

// Primer nombre + primer apellido: para que quepan todas las columnas de la
// matriz de un vistazo sin que el scroll lateral se dispare con nombres
// compuestos largos.
function shortTenantName(tenantId, fullNameFallback, tenantsList) {
  const t = tenantsList.find((x) => x.id === tenantId);
  if (t) {
    const nombre = (t.nombre || "").trim().split(/\s+/)[0] || "";
    const apellido = (t.apellidos || "").trim().split(/\s+/)[0] || "";
    const short = `${nombre} ${apellido}`.trim();
    if (short) return short;
  }
  return (fullNameFallback || "").trim().split(/\s+/).slice(0, 2).join(" ");
}

// Texto de tooltip con el/los periodo(s) de contrato del inquilino en este
// piso, para el nombre de columna: "de cuándo a cuándo es el contrato".
function contractPeriodTooltip(tenantId, contractsList) {
  const own = contractsList
    .filter((c) => c.tenant_id === tenantId)
    .sort((a, b) => (a.fecha_inicio || "").localeCompare(b.fecha_inicio || ""));
  if (own.length === 0) return "";
  const lines = own.map((c) => {
    const inicio = fechaCorta(c.fecha_inicio);
    let fin;
    if (c.estado === "finalizado" && c.fecha_fin_real) fin = fechaCorta(c.fecha_fin_real);
    else if (c.prorroga_indefinida) fin = "prórroga indefinida";
    else if (c.fecha_fin) fin = fechaCorta(c.fecha_fin);
    else fin = "—";
    let line = `${inicio} – ${fin}`;
    if (c.recortado) line += " (recortado)";
    return line;
  });
  const etiqueta = own.length > 1 ? "Contratos" : "Contrato";
  return `${etiqueta}:\n${lines.join("\n")}`;
}

function parseDateOnlyUtc(yyyyMmDd) {
  if (!yyyyMmDd || typeof yyyyMmDd !== "string") return null;
  const parts = yyyyMmDd.split("-").map((x) => Number(x));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  return Date.UTC(parts[0], parts[1] - 1, parts[2]);
}

// Códigos de color por estado, consistentes con el resto de la app:
// success ya significa "Pagado" en TenantDetail, así que "Futuro" pasa a
// primary (como el resto de estados "futuros" en Dashboard) para no
// compartir el mismo verde que un pago ya cobrado.
const STATUS_META = {
  pagado: { label: "Pagado", badge: "bg-success/15 text-success" },
  vencido: { label: "Vencido", badge: "bg-destructive/15 text-destructive" },
  en_plazo: { label: "En plazo", badge: "bg-warning/15 text-warning" },
  futuro: { label: "Futuro", badge: "bg-primary/15 text-primary" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status];
  if (!meta) return <span className="text-muted-foreground">—</span>;
  return <Badge className={`${meta.badge} hover:${meta.badge} border-0`}>{meta.label}</Badge>;
}

function ymOf(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.getUTCFullYear() * 100 + (d.getUTCMonth() + 1);
}

// Si el mes cae después de que ya haya terminado el (único o último) contrato
// del inquilino ese piso —recortado o simplemente finalizado— ya no hace
// falta ningún pago ahí: se distingue de una celda vacía normal (antes de
// empezar el contrato, o dentro de un hueco entre contratos).
function contractGapInfo(tenantId, mes, anio, contractsList) {
  const cellYm = anio * 100 + mes;
  const own = contractsList.filter((c) => c.tenant_id === tenantId);
  if (!own.length) return null;

  // Los periodos de facturación van por el día de firma del contrato, no
  // por mes natural: el último tramo (p. ej. del 23 de un mes al 23 del
  // siguiente) ya se cobra dentro del pago fechado el mes anterior, así que
  // el mes en que el contrato termina a mitad de mes se queda sin pago
  // propio aunque el contrato lo cubra. Se distingue de una cobertura
  // "normal" para poder explicarlo en vez de dejar la celda en blanco sin
  // más.
  let normalCoverage = false;
  let terminalNote = null;
  own.forEach((c) => {
    const startYm = ymOf(c.fecha_inicio);
    if (startYm == null || cellYm < startYm) return;
    if (c.prorroga_indefinida) { normalCoverage = true; return; }
    const endSrc = c.fecha_fin_real || c.fecha_fin;
    if (!endSrc) { if (c.estado !== "finalizado") normalCoverage = true; return; }
    const endYm = ymOf(endSrc);
    if (endYm == null || cellYm < endYm) { normalCoverage = true; return; }
    if (cellYm === endYm) {
      terminalNote = `Contrato hasta el ${fechaCorta(endSrc)} — el alquiler de este tramo va incluido en el pago del mes anterior`;
    }
  });
  if (normalCoverage) return null;
  if (terminalNote) return terminalNote;

  // Se ignora el campo "estado": un contrato en su último tramo puede seguir
  // marcado como "activo" (nadie lo ha finalizado a mano todavía) aunque su
  // fecha de fin ya haya pasado; lo que importa aquí es la fecha, para que
  // el hueco siempre apunte al contrato que de verdad cubrió ese periodo más
  // recientemente, no al penúltimo contrato del inquilino.
  const ended = own
    .filter((c) => !c.prorroga_indefinida && (c.fecha_fin_real || c.fecha_fin))
    .map((c) => ({ c, endYm: ymOf(c.fecha_fin_real || c.fecha_fin) }))
    .filter((x) => x.endYm != null && cellYm > x.endYm)
    .sort((a, b) => b.endYm - a.endYm)[0];
  if (!ended) return null;

  const { c } = ended;
  const motivo = c.recortado ? "recortado" : "finalizado";
  return `Contrato ${motivo} el ${fechaCorta(c.fecha_fin_real || c.fecha_fin)} — no aplica pago`;
}

// Celda de la matriz: importe coloreado según su estado, que al hacer clic
// abre el modal de validación (o "—" si ese inquilino no tiene pago ese mes,
// con un estilo distinto si es porque el contrato se recortó y ya no aplica).
const GAP_CLASS = "text-center text-sm py-1.5 rounded border border-dashed border-muted-foreground/30 text-muted-foreground/40";

function PaymentCell({ payment, status, gapReason, onClick }) {
  if (!payment) {
    if (gapReason) return <div title={gapReason} className={GAP_CLASS}>—</div>;
    return <div className="text-center text-muted-foreground/30 text-sm py-1.5">—</div>;
  }
  // Importe 0 (habitual en meses tras un contrato recortado/finalizado que
  // conservan el registro en vez de borrarlo): mismo estilo neutro que un
  // hueco, pero sigue siendo clicable porque es un pago real editable.
  if (Number(payment.importe) === 0) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={gapReason || "Importe 0 — no aplica pago"}
        data-testid={`payment-cell-${payment.id}`}
        className={`w-full hover:opacity-80 transition-opacity ${GAP_CLASS}`}
      >
        —
      </button>
    );
  }
  const meta = STATUS_META[status] || STATUS_META.futuro;
  return (
    <button
      type="button"
      onClick={onClick}
      title={meta.label}
      data-testid={`payment-cell-${payment.id}`}
      className={`w-full rounded px-2 py-1.5 text-right font-mono text-sm transition-opacity hover:opacity-80 ${meta.badge}`}
    >
      {eur(payment.importe)}
    </button>
  );
}

function YearSelect({ value, onChange, anios }) {
  const idx = anios.indexOf(Number(value));
  const step = (delta) => {
    const next = idx + delta;
    if (next < 0 || next >= anios.length) return;
    onChange(String(anios[next]));
  };
  return (
    <div className="flex items-center gap-1">
      <Label className="text-xs whitespace-nowrap mr-1">Año:</Label>
      <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => step(-1)} disabled={idx <= 0} data-testid="payments-year-prev">
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-24 h-8 text-xs" data-testid="payments-year-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          {anios.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => step(1)} disabled={idx === -1 || idx >= anios.length - 1} data-testid="payments-year-next">
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

// Modal único para validar cualquier pago (mensualidad o fianza): marcar como
// pagado, fijar la fecha real de ingreso y, si hace falta, corregir el importe.
function ValidatePaymentDialog({ payment, tenantName, tenantTooltip, onOpenChange, onSave, onDelete }) {
  const open = !!payment;
  const [realizado, setRealizado] = useState(false);
  const [fechaCobro, setFechaCobro] = useState("");
  const [importe, setImporte] = useState("");

  useEffect(() => {
    if (payment) {
      setRealizado(!!payment.realizado);
      setFechaCobro(payment.fecha_cobro ? payment.fecha_cobro.slice(0, 10) : "");
      setImporte(String(payment.importe ?? ""));
    }
  }, [payment]);

  const handleSave = () => {
    const num = Number(importe);
    if (Number.isNaN(num) || num < 0) return toast.error("Importe inválido");
    onSave(payment.id, {
      realizado,
      importe: num,
      fecha_cobro: realizado && fechaCobro ? new Date(`${fechaCobro}T12:00:00`).toISOString() : null,
    });
  };

  const periodo = payment
    ? `${MESES_NOMBRE[Number((payment.fecha || "").slice(5, 7)) - 1] || ""} ${(payment.fecha || "").slice(0, 4)}`
    : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {payment && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Validar pago</DialogTitle>
              <DialogDescription>
                <Link to={`/inquilinos/${payment.tenant_id}`} title={tenantTooltip} className="hover:text-primary hover:underline">
                  {tenantName}
                </Link>
                {" "}· {payment.concepto} · {periodo}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-md bg-secondary px-3 py-2 flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Fecha de pago</span>
                <span className="font-mono font-medium">{fechaCorta(payment.fecha)}</span>
              </div>
              <div className="flex items-center justify-between rounded-md border border-border p-3">
                <p className="text-sm font-medium">Marcado como pagado</p>
                <Switch
                  checked={realizado}
                  onCheckedChange={setRealizado}
                  data-testid="validate-payment-switch"
                />
              </div>
              {realizado && (
                <div>
                  <Label>Fecha de ingreso</Label>
                  <Input type="date" value={fechaCobro} onChange={(e) => setFechaCobro(e.target.value)} data-testid="validate-payment-fecha" />
                </div>
              )}
              <div>
                <Label>Importe (€)</Label>
                <Input type="number" min="0" step="0.01" value={importe} onChange={(e) => setImporte(e.target.value)} data-testid="validate-payment-importe" />
              </div>
            </div>
            <DialogFooter className="sm:justify-between">
              <button type="button" onClick={() => onDelete(payment.id)} className="text-sm text-muted-foreground hover:text-destructive">
                Eliminar pago
              </button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                <Button onClick={handleSave} data-testid="validate-payment-save">Guardar</Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Payments() {
  const { selectedId } = useProperty();
  const [payments, setPayments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ tenant_id: "", fecha: "", fecha_cobro: "", importe: 430, realizado: false, concepto: "Alquiler mensual" });
  const [validatingPayment, setValidatingPayment] = useState(null);
  const [fianzaBusyId, setFianzaBusyId] = useState(null);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const [selectedYear, setSelectedYear] = useState(String(currentYear));

  const load = () => {
    if (!selectedId) return setLoading(false);
    setLoading(true);
    Promise.all([
      api.get(`/payments?property_id=${selectedId}`),
      api.get(`/tenants?property_id=${selectedId}`),
      api.get(`/contracts?property_id=${selectedId}`),
    ]).then(([p, t, c]) => { setPayments(p.data); setTenants(t.data); setContracts(c.data); }).finally(() => setLoading(false));
  };
  useEffect(load, [selectedId]);

  const today = now.toISOString().slice(0, 10);

  const paymentPlazo = useCallback((p) => {
    if (!p || p.realizado) return null;
    const due = parseDateOnlyUtc((p.fecha || "").slice(0, 10));
    const t = parseDateOnlyUtc(today);
    if (due == null || t == null) return null;
    const daysLate = Math.floor((t - due) / DAY_MS);
    if (daysLate > GRACE_DAYS) return "vencido";
    if (daysLate >= 0) return "en_plazo";
    return "futuro";
  }, [today]);

  const statusOf = useCallback((p) => (p.realizado ? "pagado" : paymentPlazo(p)), [paymentPlazo]);

  const rentPayments = useMemo(() => payments.filter(isRentPayment), [payments]);

  const tenantFullName = useCallback((tenantId) => {
    const t = tenants.find((x) => x.id === tenantId);
    if (!t) return "—";
    return `${t.nombre || ""} ${t.apellidos || ""}`.trim() || "—";
  }, [tenants]);

  // Historial de fianzas: una fila por inquilino (agrupando todos sus
  // contratos en este piso, igual que hace el backend al devolver la
  // fianza), no por pago suelto — así se ve el ciclo completo aunque el
  // depósito nunca llegara a generar una transacción propia (esquema
  // "legacy": fusionado en el primer alquiler) o si el inquilino renovó
  // varias veces (la devolución se registra una sola vez, al final).
  const fianzaHistory = useMemo(() => {
    const byTenant = new Map();
    contracts.forEach((c) => {
      if (!byTenant.has(c.tenant_id)) byTenant.set(c.tenant_id, []);
      byTenant.get(c.tenant_id).push(c);
    });

    const rows = [];
    byTenant.forEach((group, tenantId) => {
      const sorted = [...group].sort((a, b) => (a.fecha_inicio || "").localeCompare(b.fecha_inicio || ""));
      const initial = sorted[0];
      const last = sorted[sorted.length - 1];
      const fianza = Number(initial.fianza || 0);
      if (fianza <= 0) return;

      const groupIds = new Set(sorted.map((c) => c.id));
      const fianzaPago = payments.find((p) => p.concepto === FIANZA_CONCEPTO && groupIds.has(p.contract_id));
      const esquema = fianzaPago ? "nuevo" : "legacy";

      // Qué pago "trae" la fianza de entrada: la fila propia (esquema nuevo)
      // o, si no existe, el primer pago del contrato inicial (esquema
      // legacy, donde va fusionada), igual que _first_payment_includes_fianza.
      const contractPayments = payments
        .filter((p) => p.contract_id === initial.id && p.concepto !== FIANZA_DEVUELTA_CONCEPTO)
        .sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
      const entradaPago = fianzaPago || contractPayments[0] || null;

      const devueltaPago = payments.find((p) => p.concepto === FIANZA_DEVUELTA_CONCEPTO && p.tenant_id === tenantId);
      const retenidaPago = payments.find((p) => isFianzaRetenidaConcepto(p.concepto) && groupIds.has(p.contract_id));
      const activo = last.estado === "activo";

      let estado;
      let movimiento = null;
      if (devueltaPago) {
        estado = "devuelta";
        movimiento = { importe: devueltaPago.importe, fecha: devueltaPago.fecha };
      } else if (retenidaPago || sorted.some((c) => c.fianza_retenida)) {
        estado = "retenida";
        movimiento = retenidaPago
          ? { importe: retenidaPago.importe, fecha: retenidaPago.fecha }
          : { importe: fianza, fecha: null };
      } else if (activo) {
        estado = "pendiente";
      } else {
        // Fallback: nadie marcó devolución ni retención. Sumamos la
        // duración de cada contrato del grupo (no solo fecha_inicio del
        // primero a fecha_fin del último) para cubrir también estancias
        // discontinuas con huecos entre contratos.
        const diasEstancia = sorted.reduce(
          (sum, c) => sum + diasEntre(c.fecha_inicio, c.fecha_fin_real || c.fecha_fin || c.fecha_inicio),
          0
        );
        if (diasEstancia >= ESTANCIA_MINIMA_DEVOLUCION_DIAS) {
          estado = "probable_devuelta";
          movimiento = { importe: -fianza, fecha: null, inferido: true };
        } else {
          estado = "sin_resolver";
        }
      }

      rows.push({
        tenantId,
        fianza,
        esquema,
        fechaEntrada: initial.fecha_inicio,
        entradaCobrada: !!entradaPago?.realizado,
        fechaSalida: activo ? null : (last.fecha_fin_real || last.fecha_fin || null),
        estado,
        movimiento,
        lastContractId: last.id,
      });
    });

    return rows.sort((a, b) => (b.fechaEntrada || "").localeCompare(a.fechaEntrada || ""));
  }, [contracts, payments]);

  // Solo los inquilinos con contrato vigente ese año (es decir, con alguna
  // mensualidad en el año seleccionado); así no aparecen columnas siempre
  // vacías de inquilinos de otros años. Se recalcula al cambiar de año.
  // Nombre corto (primer nombre + primer apellido) para que quepan todas
  // las columnas de un vistazo sin disparar el scroll lateral.
  const tenantColumns = useMemo(() => {
    const map = new Map();
    rentPayments.forEach((p) => {
      if (Number((p.fecha || "").slice(0, 4)) !== Number(selectedYear)) return;
      // Importe 0 = placeholder que queda tras recortar/finalizar un contrato
      // (se conserva el registro en vez de borrarlo), no un pago efectivo: no
      // debe bastar por sí solo para sacar una columna vacía todo el año.
      if (Number(p.importe) === 0) return;
      if (!map.has(p.tenant_id)) map.set(p.tenant_id, p.tenant_nombre || "—");
    });
    return [...map.entries()]
      .map(([id, nombreCompleto]) => ({
        id,
        nombre: shortTenantName(id, nombreCompleto, tenants),
        tooltip: contractPeriodTooltip(id, contracts),
      }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [rentPayments, selectedYear, tenants, contracts]);

  // Rango continuo (sin huecos) para que las flechas de año siempre aterricen
  // en una opción válida del desplegable. Un año futuro sí puede aparecer si
  // ya hay algún pago real fechado ahí (p. ej. un inquilino que paga
  // anticipado la mensualidad del 1 de enero durante diciembre) — así se
  // puede seguir validando ese pago en el momento.
  const anios = useMemo(() => {
    const years = rentPayments.map((p) => Number((p.fecha || "").slice(0, 4))).filter(Boolean);
    years.push(currentYear);
    const min = Math.min(...years);
    const max = Math.max(...years);
    const out = [];
    for (let y = min; y <= max; y++) out.push(y);
    return out;
  }, [rentPayments, currentYear]);

  const monthsMatrix = useMemo(() => {
    return MESES_NOMBRE.map((nombre, idx) => {
      const mes = idx + 1;
      const cells = tenantColumns.map((col) => {
        const payment = rentPayments.find((p) =>
          p.tenant_id === col.id &&
          Number((p.fecha || "").slice(0, 4)) === Number(selectedYear) &&
          Number((p.fecha || "").slice(5, 7)) === mes
        ) || null;
        const gapReason = contractGapInfo(col.id, mes, Number(selectedYear), contracts);
        return { tenantId: col.id, payment, gapReason };
      });
      const total = cells.reduce((s, c) => s + (c.payment ? Number(c.payment.importe || 0) : 0), 0);
      return { mes, nombre, cells, total };
    });
  }, [tenantColumns, rentPayments, selectedYear, contracts]);

  const tenantTotals = useMemo(
    () => tenantColumns.map((col) => ({
      id: col.id,
      total: monthsMatrix.reduce((s, row) => s + (row.cells.find((c) => c.tenantId === col.id)?.payment?.importe || 0), 0),
    })),
    [tenantColumns, monthsMatrix]
  );
  const grandTotal = monthsMatrix.reduce((s, row) => s + row.total, 0);

  const totalCobrado = payments.filter((p) => p.realizado).reduce((s, p) => s + p.importe, 0);
  const totalPendiente = payments
    .filter((p) => !p.realizado && ["vencido", "en_plazo"].includes(paymentPlazo(p)))
    .reduce((s, p) => s + p.importe, 0);

  const handleValidateSave = async (id, patch) => {
    setPayments((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    setValidatingPayment(null);
    try {
      await api.put(`/payments/${id}`, patch);
      toast.success("Pago actualizado");
    } catch {
      toast.error("Error al actualizar");
      load();
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este pago?")) return;
    setValidatingPayment(null);
    try {
      await api.delete(`/payments/${id}`);
      toast.success("Pago eliminado");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const markFianza = async (row, tipo) => {
    const body = tipo === "devuelta"
      ? { fianza_devuelta: true, fianza_retenida: false }
      : { fianza_retenida: true, fianza_devuelta: false };
    setFianzaBusyId(row.tenantId);
    try {
      await api.put(`/contracts/${row.lastContractId}`, body);
      toast.success(tipo === "devuelta" ? "Fianza marcada como devuelta" : "Fianza marcada como retenida");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar la fianza");
    } finally {
      setFianzaBusyId(null);
    }
  };

  const saveAdd = async () => {
    if (!addForm.tenant_id || !addForm.fecha) return toast.error("Inquilino y fecha obligatorios");
    if (Number(addForm.importe) < 0) return toast.error("El importe no puede ser negativo");
    try {
      const body = {
        property_id: selectedId, tenant_id: addForm.tenant_id,
        fecha: new Date(addForm.fecha).toISOString(), importe: Number(addForm.importe) || 0,
        realizado: addForm.realizado, concepto: addForm.concepto,
      };
      if (addForm.fecha_cobro) body.fecha_cobro = new Date(addForm.fecha_cobro).toISOString();
      await api.post("/payments", body);
      toast.success("Pago añadido");
      setAddOpen(false);
      load();
    } catch { toast.error("Error al guardar"); }
  };

  const tenantNameFor = (p) => p?.tenant_nombre || tenants.find((t) => t.id === p?.tenant_id)?.nombre || "—";

  if (!selectedId)
    return <div className="data-card p-10 text-center text-muted-foreground">Selecciona o crea un piso primero.</div>;

  return (
    <div data-testid="payments-page">
      <PageHeader title="Pagos" subtitle="Control de cobros">
        <Button
          onClick={() => { setAddForm({ tenant_id: "", fecha: "", fecha_cobro: "", importe: 430, realizado: false, concepto: "Alquiler mensual" }); setAddOpen(true); }}
          data-testid="add-payment-button"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Añadir pago
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="data-card p-4"><p className="overline">Cobrado</p><p className="font-mono text-xl font-semibold text-success mt-1">{eur(totalCobrado)}</p></div>
        <div className="data-card p-4"><p className="overline">Pendiente</p><p className="font-mono text-xl font-semibold text-warning mt-1">{eur(totalPendiente)}</p></div>
      </div>

      <Tabs defaultValue="mensuales">
        <TabsList>
          <TabsTrigger value="mensuales" data-testid="tab-mensuales">Alquileres mensuales</TabsTrigger>
          <TabsTrigger value="fianzas" data-testid="tab-fianzas">Fianzas</TabsTrigger>
        </TabsList>

        <TabsContent value="mensuales">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
            <YearSelect value={selectedYear} onChange={setSelectedYear} anios={anios} />
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {["futuro", "en_plazo", "vencido", "pagado"].map((k) => (
                <span key={k} className="flex items-center gap-1.5">
                  <span className={`h-2.5 w-2.5 rounded-sm inline-block ${STATUS_META[k].badge.split(" ")[0]}`} />
                  {STATUS_META[k].label}
                </span>
              ))}
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm inline-block border border-dashed border-muted-foreground/40" />
                Contrato finalizado
              </span>
            </div>
          </div>

          <div className="data-card overflow-x-auto">
            <Table data-testid="payments-matrix">
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-card">Mes</TableHead>
                  {tenantColumns.map((t) => (
                    <TableHead key={t.id} className="text-right whitespace-nowrap min-w-[88px]">
                      <Link
                        to={`/inquilinos/${t.id}`}
                        title={t.tooltip}
                        className="hover:text-primary hover:underline"
                        data-testid={`tenant-link-${t.id}`}
                      >
                        {t.nombre}
                      </Link>
                    </TableHead>
                  ))}
                  <TableHead className="text-right font-semibold min-w-[90px]">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={tenantColumns.length + 2} className="text-center py-8 text-muted-foreground">Cargando…</TableCell></TableRow>
                ) : tenantColumns.length === 0 ? (
                  <TableRow><TableCell colSpan={2} className="text-center py-8 text-muted-foreground">Sin pagos de alquiler</TableCell></TableRow>
                ) : (
                  monthsMatrix.map((row) => {
                    const isCurrent = row.mes === currentMonth && Number(selectedYear) === currentYear;
                    return (
                      <TableRow key={row.mes} data-testid={`payments-row-${row.mes}`} className={isCurrent ? "bg-primary/10" : ""}>
                        <TableCell className={`sticky left-0 font-medium whitespace-nowrap ${isCurrent ? "bg-primary/10" : "bg-card"}`}>
                          {row.nombre}
                        </TableCell>
                        {row.cells.map((c) => (
                          <TableCell key={c.tenantId} className="p-1">
                            <PaymentCell
                              payment={c.payment}
                              status={c.payment ? statusOf(c.payment) : null}
                              gapReason={c.gapReason}
                              onClick={() => setValidatingPayment(c.payment)}
                            />
                          </TableCell>
                        ))}
                        <TableCell className="text-right font-mono font-semibold">{eur(row.total)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
              {tenantColumns.length > 0 && (
                <tfoot>
                  <TableRow className="border-t-2 border-border bg-secondary/50">
                    <TableCell className="sticky left-0 bg-secondary/50 font-semibold">TOTAL</TableCell>
                    {tenantTotals.map((t) => (
                      <TableCell key={t.id} className="text-right font-mono font-semibold text-sm">{eur(t.total)}</TableCell>
                    ))}
                    <TableCell className="text-right font-mono font-bold">{eur(grandTotal)}</TableCell>
                  </TableRow>
                </tfoot>
              )}
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="fianzas">
          <div className="flex flex-wrap items-center gap-3 mb-3 text-xs text-muted-foreground">
            {Object.entries(ESTADO_FIANZA_META).map(([k, meta]) => (
              <span key={k} className="flex items-center gap-1.5">
                <span className={`h-2.5 w-2.5 rounded-sm inline-block ${meta.badge.split(" ")[0]}`} />
                {meta.label}
              </span>
            ))}
          </div>

          <div className="data-card overflow-x-auto">
            <Table data-testid="fianzas-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Inquilino</TableHead>
                  <TableHead className="text-right">Fianza</TableHead>
                  <TableHead>Entrada</TableHead>
                  <TableHead>Salida</TableHead>
                  <TableHead className="hidden lg:table-cell">Esquema</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Movimiento</TableHead>
                  <TableHead className="w-1"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fianzaHistory.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Sin fianzas registradas</TableCell></TableRow>
                ) : (
                  fianzaHistory.map((r) => {
                    const meta = ESTADO_FIANZA_META[r.estado];
                    return (
                      <TableRow key={r.tenantId} data-testid={`fianza-row-${r.tenantId}`}>
                        <TableCell className="font-medium">
                          <Link
                            to={`/inquilinos/${r.tenantId}`}
                            title={contractPeriodTooltip(r.tenantId, contracts)}
                            className="hover:text-primary hover:underline"
                          >
                            {tenantFullName(r.tenantId)}
                          </Link>
                        </TableCell>
                        <TableCell className="text-right font-mono">{eur(r.fianza)}</TableCell>
                        <TableCell className="font-mono text-sm whitespace-nowrap">
                          {fechaCorta(r.fechaEntrada)}
                          {!r.entradaCobrada && (
                            <span className="ml-1.5 text-xs text-warning">(pendiente de cobro)</span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{r.fechaSalida ? fechaCorta(r.fechaSalida) : "—"}</TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <Badge variant="outline" className="font-normal text-muted-foreground">
                            {r.esquema === "nuevo" ? "Nuevo" : "Legacy"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${meta.badge} hover:${meta.badge} border-0`}>{meta.label}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {r.movimiento ? (
                            <>
                              {eur(r.movimiento.importe)}
                              {r.movimiento.fecha ? (
                                <div className="text-xs text-muted-foreground">{fechaCorta(r.movimiento.fecha)}</div>
                              ) : r.movimiento.inferido ? (
                                <div className="text-xs text-muted-foreground italic">estimado</div>
                              ) : null}
                            </>
                          ) : "—"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {(r.estado === "sin_resolver" || r.estado === "probable_devuelta") && (
                            <div className="flex gap-1.5 justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                disabled={fianzaBusyId === r.tenantId}
                                onClick={() => markFianza(r, "devuelta")}
                                data-testid={`fianza-mark-devuelta-${r.tenantId}`}
                              >
                                Devuelta
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                disabled={fianzaBusyId === r.tenantId}
                                onClick={() => markFianza(r, "retenida")}
                                data-testid={`fianza-mark-retenida-${r.tenantId}`}
                              >
                                Retenida
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Añadir pago</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Inquilino *</Label>
              <Select value={addForm.tenant_id} onValueChange={(v) => setAddForm((f) => ({ ...f, tenant_id: v }))}>
                <SelectTrigger data-testid="payment-tenant-select"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>{tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.nombre} {t.apellidos}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fecha *</Label><Input type="date" value={addForm.fecha} onChange={(e) => setAddForm((f) => ({ ...f, fecha: e.target.value }))} /></div>
              <div><Label>Importe (€)</Label><Input type="number" min="0" value={addForm.importe} onChange={(e) => setAddForm((f) => ({ ...f, importe: e.target.value }))} /></div>
            </div>
            <div><Label>Fecha de cobro (opcional)</Label><Input type="date" value={addForm.fecha_cobro} onChange={(e) => setAddForm((f) => ({ ...f, fecha_cobro: e.target.value }))} /></div>
            <div><Label>Concepto</Label><Input value={addForm.concepto} onChange={(e) => setAddForm((f) => ({ ...f, concepto: e.target.value }))} /></div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <p className="text-sm font-medium">Marcado como pagado</p>
              <Switch checked={addForm.realizado} onCheckedChange={(v) => setAddForm((f) => ({ ...f, realizado: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancelar</Button>
            <Button onClick={saveAdd} data-testid="save-payment-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ValidatePaymentDialog
        payment={validatingPayment}
        tenantName={tenantNameFor(validatingPayment)}
        tenantTooltip={validatingPayment ? contractPeriodTooltip(validatingPayment.tenant_id, contracts) : ""}
        onOpenChange={(v) => { if (!v) setValidatingPayment(null); }}
        onSave={handleValidateSave}
        onDelete={remove}
      />
    </div>
  );
}
